import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Landmark, Building2, ClipboardList, RefreshCw, Radar, Target } from "lucide-react";

const KIND_ICON = { grant: Landmark, bank: Building2, register: ClipboardList };
const COVER_SEGS = [
  ["secured_grants", "Secured grants", "#00E65B"],
  ["bank_secured", "Bank approved", "#00B4E6"],
  ["pending_grants", "Pending grants", "#FFB800"],
  ["bank_requested", "Bank requested", "#7DD3FC"],
  ["internal_gap", "Internal / unfunded", "#f87171"],
];
const FLOW_SEGS = [
  ["committed", "Committed", "#00E65B"],
  ["in_approval", "In approval", "#FFB800"],
  ["pipeline", "Pipeline", "#5b5e66"],
];

function SegBar({ total, segs, values, testid }) {
  if (!total) return null;
  return (
    <>
      <div className="flex h-4 rounded-sm overflow-hidden border border-border" data-testid={testid}>
        {segs.map(([k, , color]) => (values[k] > 0 &&
          <div key={k} style={{ width: `${(values[k] / total) * 100}%`, background: color, opacity: 0.85 }} title={k} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {segs.map(([k, label, color]) => (
          <span key={k} className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm inline-block" style={{ background: color }} />
            {label} <span className="font-mono text-foreground">{formatSgd(values[k] || 0)}</span>
          </span>
        ))}
      </div>
    </>
  );
}

function timeAgo(iso) {
  try {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 3600) return `${Math.max(1, Math.round(d / 60))}m ago`;
    if (d < 86400) return `${Math.round(d / 3600)}h ago`;
    return `${Math.round(d / 86400)}d ago`;
  } catch { return ""; }
}

export default function CapitalTwin() {
  const [d, setD] = useState(null);
  const load = () => api.get("/susfin/capital-twin").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Building the capital twin…</div>;
  if (d.empty) return <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-twin-empty">No data yet.</div>;

  const w = d.waterfall;
  const maxStage = Math.max(1, ...d.stages.map((s) => s.capex));

  return (
    <div className="space-y-4" data-testid="sf-capital-twin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Live model of your whole capital position — recomputed from the register, grant applications and financing cases every time it loads.
        </p>
        <button onClick={() => { setD(null); load(); }} className="text-xs text-accent inline-flex items-center gap-1 hover:underline" data-testid="sf-twin-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh · as of {d.generated_at?.slice(11, 19)} UTC
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Total plan CAPEX", w.total_plan, ""], ["Committed", w.committed, "text-[#00E65B]"],
          ["In approval", w.in_approval, "text-[#FFB800]"], ["Pipeline", w.pipeline, "text-muted-foreground"]].map(([l, v, tone]) => (
          <div key={l} className="card-flat p-4" data-testid={`sf-twin-card-${String(l).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="label-eyebrow text-[10px] mb-1">{l}</div>
            <div className={`font-mono text-xl font-bold ${tone}`}>{formatSgd(v)}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-flat p-5" data-testid="sf-twin-flow">
          <div className="label-eyebrow mb-3">Capital deployment</div>
          <SegBar total={w.total_plan} segs={FLOW_SEGS} values={w} testid="sf-twin-flow-bar" />
          <div className="label-eyebrow text-[10px] mt-5 mb-2">Capital by pipeline stage</div>
          <div className="grid grid-cols-7 gap-2">
            {d.stages.map((s) => (
              <div key={s.value} className="text-center" data-testid={`sf-twin-stage-${s.value}`}>
                <div className="h-12 flex items-end justify-center mb-1">
                  <div className={`w-full max-w-[36px] rounded-t-sm ${s.capex ? "bg-accent/70" : "bg-border"}`}
                    style={{ height: `${Math.max(6, (s.capex / maxStage) * 100)}%` }} />
                </div>
                <div className="font-mono text-[10px] font-bold">{s.capex ? `$${formatNum(Math.round(s.capex / 1000))}k` : "—"}</div>
                <div className="text-[8px] text-muted-foreground leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat p-5" data-testid="sf-twin-coverage">
          <div className="label-eyebrow mb-1">Funding coverage</div>
          <div className="text-[11px] text-muted-foreground mb-3">
            <span className="text-[#00E65B] font-mono">{d.coverage.hard_pct}%</span> secured ·{" "}
            <span className="text-[#FFB800] font-mono">{d.coverage.soft_pct}%</span> in motion vs total plan
          </div>
          <SegBar total={w.total_plan} segs={COVER_SEGS} values={d.coverage} testid="sf-twin-coverage-bar" />
          {d.gaps.length > 0 && (
            <>
              <div className="label-eyebrow text-[10px] mt-5 mb-2">Target-driven capital gaps</div>
              <div className="space-y-1.5">
                {d.gaps.map((g) => (
                  <div key={g.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`sf-twin-gap-${g.id}`}>
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground truncate">
                      <Target className="h-3 w-3 text-accent shrink-0" /> {g.name} (FY{g.target_year})
                    </span>
                    <span className={`font-mono font-bold shrink-0 text-xs ${g.capital_gap > 0 ? "text-red-400" : g.residual_gap > 0 ? "text-[#FFB800]" : "text-[#00E65B]"}`}>
                      {g.capital_gap > 0 ? formatSgd(g.capital_gap) : g.residual_gap > 0 ? "no funded plan yet" : "closed"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card-flat p-5" data-testid="sf-twin-movements">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><Radar className="h-3.5 w-3.5 text-accent" /> Recent capital movements</div>
        <div className="space-y-2">
          {d.movements.map((m, idx) => {
            const Icon = KIND_ICON[m.kind] || ClipboardList;
            return (
              <div key={idx} className="flex items-center gap-3 border border-border/50 rounded-sm px-3 py-2" data-testid={`sf-twin-move-${idx}`}>
                <Icon className="h-3.5 w-3.5 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{String(m.label || "").replace(/_/g, " ")}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{m.detail}</div>
                </div>
                <Badge variant="outline" className="text-[8px] uppercase shrink-0">{m.kind}</Badge>
                <span className="text-[10px] text-muted-foreground font-mono shrink-0 w-14 text-right">{timeAgo(m.at)}</span>
              </div>
            );
          })}
          {d.movements.length === 0 && <p className="text-xs text-muted-foreground">No capital movements recorded yet.</p>}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
